import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PulseLoader } from "react-spinners";
import { useAtom } from "jotai";
import logo from "../assets/logo.png";
import TextField from "../components/TextField";
import { useSignUp } from "../hooks/login/useSignUp";
import { userAtom, userTokenAtom } from "../atoms/globalStates";
import { useGetBranches } from "../hooks/branches/useGet";
import { useGetRoles } from "../hooks/roles/useGet";
import Select from "../components/Select";

// util: derive age from DOB (yyyy-mm-dd)
const calcAge = (dobStr) => {
  if (!dobStr) return 0;
  const dob = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return Math.max(0, age);
};

const SignUp = () => {
  const navigate = useNavigate();
  // You likely won't set atoms on signup, but keeping them here for parity if you later auto-login.
  const [, setUserData] = useAtom(userAtom);
  const [, setUserToken] = useAtom(userTokenAtom);

  // Keep keys in **PascalCase** to match backend DTO binder.
  const [form, setForm] = useState({
    Login: "",
    Email: "",
    FirstName: "",
    LastName: "",
    Name: "",        // full name (we'll auto-fill First + Last, but allow override)
    Password: "",
    Job: "",
    DOB: "",         // yyyy-mm-dd
    age: 0,          // auto-derived from DOB
    Role: 0,         // number (role id)
    Branch: 0,       // number (branch id)
    TellerId: 0,     // optional; keep 0 or empty if not a teller
  });

  const [err, setErr] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { data: branches, isSuccess: gotBranches } = useGetBranches(1,100);
      const {data: roles, isSuccess: gotRoles} = useGetRoles()


        const branchOptions = useMemo(
    () =>
      branches?.map((b) => ({
        name: `${b.branchName}`,
        value: b.branchId,
      })) ?? [],
    [branches]
  );

      const roleOptions = useMemo(
    () =>
      roles?.map((b) => ({
        name: `${b.roleName}`,
        value: b.roleId,
      })) ?? [],
    [roles]
  );


  
  

  // auto-fill Name and age
  useEffect(() => {
    const fullName = `${form.FirstName ?? ""} ${form.LastName ?? ""}`.trim();
    setForm((f) => ({
      ...f,
      Name: fullName || f.Name,
      age: calcAge(f.DOB),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.FirstName, form.LastName, form.DOB]);

  const { mutate: registerUser, data, isLoading, isSuccess, error } = useSignUp();

  const handleChange = (key) => (e) => {
    const val = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: val }));
    if (err) setErr("");
  };

  const canSubmit = useMemo(() => {
    const required =
      form.Login &&
      form.Email &&
      form.FirstName &&
      form.LastName &&
      form.Name &&
      form.Password &&
      form.Job &&
      form.DOB &&
      Number.isFinite(Number(form.Role)) &&
      Number.isFinite(Number(form.Branch));
    const pwOk = form.Password && confirmPassword && form.Password === confirmPassword;
    return required && pwOk;
  }, [form, confirmPassword]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setErr("Please complete all required fields and ensure passwords match.");
      return;
    }
    // Ensure numeric fields are numbers (model binder is tolerant, but be explicit)
    const payload = {
      ...form,
      Role: Number(form.Role),
      Branch: Number(form.Branch),
      TellerId: Number(form.TellerId || 0),
      age: Number(form.age || 0),
    };

    registerUser(payload, {
      onSuccess: () => {
        // If your API returns a token & user, you could auto-login here.
        // For now, send them to login.
        navigate("/login", { replace: true });
      },
    });
  };

  useEffect(() => {
    if (error) {
      // Prefer server message if present
      setErr(error?.response?.data?.message || "Failed to create account.");
    }
  }, [error]);

  return (
    <div className="min-h-screen min-w-full flex flex-col items-center justify-center px-2 select-none bg-gray-100">
      <img style={{ width: 80, height: 80, borderRadius: 10 }} src={logo} alt="Fms logo" />
      <p className="text-2xl bg-clip-text text-transparent bg-gradient-to-r from-[#0A0D36] to-[#b13f04] font-medium mt-2">
        {/* Create Account */}
      </p>

      <section className="bg-white rounded-md border shadow-md p-6 w-full md:w-2/3 lg:w-2/5 xl:w-[650px] mt-4">
        {err && <p className="text-start text-sm text-red-600 mb-2">{err}</p>}

        <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={handleSubmit}>
          <TextField label="Login (Username)" required value={form.Login} onChange={handleChange("Login")} />
          <TextField label="Email" type="email" required value={form.Email} onChange={handleChange("Email")} />

          <TextField label="First Name" required value={form.FirstName} onChange={handleChange("FirstName")} />
          <TextField label="Last Name" required value={form.LastName} onChange={handleChange("LastName")} />

          <TextField
            label="Full Name"
            required
            value={form.Name}
            onChange={handleChange("Name")}
            helperText="Auto-filled from first/last. You can edit."
          />

          <TextField label="Job Title" required value={form.Job} onChange={handleChange("Job")} />

          <TextField label="Date of Birth" type="date" required value={form.DOB} onChange={handleChange("DOB")} />
          <TextField label="Age" type="number" value={form.age} onChange={handleChange("age")} />

          <TextField label="Role (ID)" type="number" required value={form.Role} onChange={handleChange("Role")} />
              {gotBranches && (
        <Select
          name="Branch"
          onChange={handleChange("Branch")}
          value={form.Branch}
          placeholder={
            "Select a branch"
          }
          label="Branch"
          options={branchOptions}
          required
        />
      )}

          {gotRoles && (
        <Select
          name="Role"
          onChange={handleChange("Role")}
          value={form.Branch}
          placeholder={
            "Select a role"
          }
          label="Role"
          options={roleOptions}
          required
        />
      )}


          <TextField
            label="Teller ID (optional)"
            type="number"
            value={form.TellerId}
            onChange={handleChange("TellerId")}
          />

          <TextField
            label="Password"
            type="password"
            required
            value={form.Password}
            onChange={handleChange("Password")}
          />
          <TextField
            label="Confirm Password"
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <div className="md:col-span-2 pt-2">
            <button
              type="submit"
              className="rounded p-1.5 text-white bg-[#0A0D36] w-full font-medium disabled:opacity-60"
              disabled={isLoading || !canSubmit}
            >
              <PulseLoader loading={isLoading} size={16} color="#fff" />
              {!isLoading && "Create Account"}
            </button>
          </div>
        </form>

        <div className="mt-4 text-center text-sm">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0A0D36] underline">
            Log in
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SignUp;
